'use client';

import type { ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';
import type { ResourcesByType, TimestampByHandleByType } from '@toolproof-core/lib/_contract/resources';
import { fromTimestampedResourcesByTypeToResourcesByType } from '@toolproof-core/lib/_pure/cosmos-data-extraction';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useResources } from '@/integrations/gcp/useFirebaseWeb';
import type { CosmosData } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type { SpaceAdapterResourceTypeInputSpec } from '@toolproof-core/visualization/adapter/_contract/adapter';

export type CosmosDataWithTimestamps = CosmosData<ResourceTypeHandle> & {
    timestampByHandleByType: TimestampByHandleByType;
};

function mergeResourceListsWithPresetFallback<TResource extends { id: string; projection: unknown }>(
    presetResources: TResource[] | undefined,
    fetchedResources: TResource[] | undefined,
): TResource[] {
    const preset = presetResources ?? [];
    const fetched = fetchedResources ?? [];

    if (fetched.length === 0) return preset;

    const presetById = new Map(preset.map((resource) => [resource.id, resource]));

    return fetched.map((resource) => {
        if (resource.projection !== null && resource.projection !== undefined) {
            return resource;
        }

        const presetResource = presetById.get(resource.id);
        if (presetResource && presetResource.projection !== null && presetResource.projection !== undefined) {
            return {
                ...resource,
                projection: presetResource.projection,
            };
        }

        return resource;
    });
}

function buildTimestampByHandleByType(itemsByType: ReturnType<typeof useResources>['items']): TimestampByHandleByType {
    const timestampByHandleByType: TimestampByHandleByType = {};

    for (const [resourceTypeHandleRaw, timestampedResources] of Object.entries(itemsByType)) {
        const timestampByHandle: Record<string, string> = {};

        for (const timestampedResource of timestampedResources ?? []) {
            const projection = timestampedResource.projection;
            if (!projection || typeof projection !== 'object') continue;

            const maybeHandle = (projection as { handle?: unknown }).handle;
            if (typeof maybeHandle !== 'string') continue;
            if (typeof timestampedResource.timestamp !== 'string') continue;

            timestampByHandle[maybeHandle] = timestampedResource.timestamp;
        }

        if (Object.keys(timestampByHandle).length === 0) continue;
        timestampByHandleByType[resourceTypeHandleRaw as ResourceTypeHandle] = timestampByHandle;
    }

    return timestampByHandleByType;
}

export type CosmosDataProviderRenderValue = {
    cosmosData: CosmosDataWithTimestamps;
    loading: boolean;
    error: Error | null;
};

type CosmosDataProviderProps = {
    resourceTypeInputSpec: SpaceAdapterResourceTypeInputSpec<ResourceTypeHandle>;
    children: (value: CosmosDataProviderRenderValue) => ReactNode;
};

export default function CosmosDataProvider(props: CosmosDataProviderProps) {
    const { resourceTypeInputSpec, children } = props;
    const { requiredResourceTypes, presetResourcesByType } = resourceTypeInputSpec;
    // console.log('[CosmosDataProvider] requiredResourceTypes', requiredResourceTypes);
    const { items, loading, error } = useResources(requiredResourceTypes as ResourceTypeHandle[]);

    const cosmosData = useMemo<CosmosDataWithTimestamps>(() => {
        const fetchedResourcesByType = fromTimestampedResourcesByTypeToResourcesByType(items);
        const timestampByHandleByType = buildTimestampByHandleByType(items);

        const resourcesByType: ResourcesByType<ResourceTypeHandle> = {
            ...(presetResourcesByType ?? {}),
        };

        for (const [resourceTypeHandleRaw, fetchedResources] of Object.entries(fetchedResourcesByType)) {
            const resourceTypeHandle = resourceTypeHandleRaw as ResourceTypeHandle;
            const presetResources = resourcesByType[resourceTypeHandle] as
                | Array<{ id: string; projection: unknown }>
                | undefined;

            resourcesByType[resourceTypeHandle] = mergeResourceListsWithPresetFallback(
                presetResources,
                fetchedResources as Array<{ id: string; projection: unknown }> | undefined,
            ) as ResourcesByType<ResourceTypeHandle>[ResourceTypeHandle];
        }

        return {
            resourcesByType,
            timestampByHandleByType,
        };
    }, [items, presetResourcesByType]);

    return children({
        cosmosData,
        loading,
        error: error ?? null,
    });
}

